/**
 * PromiseCache
 *
 * A lightweight utility to cache and track in-flight (ongoing) API calls.
 * Prevents duplicate requests by caching the promise tied to a specific callId.
 * Automatically removes the call from the cache once the promise is settled.
 *
 * Use cases:
 * - Deduplicate concurrent API calls with the same parameters
 * - Optimize resource usage by preventing unnecessary network requests
 * - Track and manage ongoing async operations
 */
class PromiseCache {
	constructor() {
		this.activeCalls = new Map();
	}

	isInProgress(callId) {
		return this.activeCalls.has(callId);
	}

	getExistingPromise(callId) {
		return this.activeCalls.get(callId) || null;
	}

	startCall(callId, promise) {
		this.activeCalls.set(callId, promise);
		promise
			.finally(() => {
				this.activeCalls.delete(callId);
			})
			.catch(() => {});

		return promise;
	}

	clearAll() {
		this.activeCalls.clear();
	}

	clearCall(callId) {
		this.activeCalls.delete(callId);
	}

	getActiveCallCount() {
		return this.activeCalls.size;
	}

	getActiveCallIds() {
		return Array.from(this.activeCalls.keys());
	}

	hasActiveCalls() {
		return this.activeCalls.size > 0;
	}
}

const promiseCache = new PromiseCache();

export default promiseCache;

export { PromiseCache };
